import { useEffect, useCallback, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../../utils/tauri';
import { subscribeTauriEvent } from '../../utils/safeTauriEvent';
import { useFtpStore } from '../../store/ftpStore';
import { planTransfers, existingNames } from '../../utils/transferConflicts';
import { registerRetry } from './transferRetry';
import { pinnedConn } from './transferPin';
import {
  baseName, errText, joinRemote, newTransferId, rowMeta, wasCancelledWhileQueued, joinLocal,
  UPLOAD_CHUNK_SIZE, arrayBufferToBase64,
} from './ftpViewHelpers';

/** FtpView's transfer engine: the global progress feed, path/chunked upload and download initiators, and the overwrite-protected batch with its conflict queue. */
export function useFtpTransferEngine({
  t,
  aliveRef,
  showNotice,
}) {
  // Global transfer-progress feed: merge into the store and refresh the
  // affected pane's listing when a transfer lands successfully.
  useEffect(() => {
    if (!isTauri()) return undefined;
    const sub = subscribeTauriEvent('ftp:transfer_progress', (evt) => {
      const p = evt?.payload || {};
      useFtpStore.getState().upsertTransfer(p);
      if (p.done && !p.error && !p.canceled) {
        const s = useFtpStore.getState();
        if (p.kind === 'upload') {
          const rp = s.getRemotePath();
          if (s.activeConnId && s.connState[s.activeConnId] === 'connected' && rp) {
            s.listRemote(rp).catch(() => {});
          }
        } else if (p.kind === 'download' && s.localPath) {
          s.listLocal(s.localPath).catch(() => {});
        }
      }
    });
    return () => sub.unlisten();
  }, []);

  /* ── Transfer initiators ─────────────────────────────────────────────── */

  // `remoteDir` lets a FOLDER upload put each file in its own subdirectory;
  // omitted, it is the pane's current path exactly as before.
  const startPathUpload = useCallback((localFilePath, name, remoteDir, meta) => {
    const s = useFtpStore.getState();
    const connId = pinnedConn(meta, s);
    if (!connId || s.connState[connId] !== 'connected') return;
    if (wasCancelledWhileQueued(meta)) return;
    const transferId = meta?.transferId || newTransferId();
    const fileName = name || baseName(localFilePath);
    const remotePath = joinRemote(remoteDir || meta?.remoteDir || s.getRemotePath(), fileName);
    s.upsertTransfer({
      ...rowMeta(meta),
      transferId,
      connId,
      kind: 'upload',
      name: fileName,
      path: remotePath,
      transferred: 0,
      total: 0,
      done: false,
      error: null,
      canceled: false,
      queued: false,
    });
    // An approved upload, re-run as it was — not a new decision, so no new prompt.
    registerRetry(transferId, () =>
      startPathUpload(localFilePath, name, remoteDir, { ...meta, transferId: undefined })
    );
    invoke('ftp_upload', {
      connId,
      transferId,
      localPath: localFilePath,
      remotePath,
    }).catch((e) => {
      useFtpStore.getState().upsertTransfer({
        transferId,
        connId,
        kind: 'upload',
        name: fileName,
        transferred: 0,
        total: 0,
        done: true,
        error: errText(e),
        canceled: false,
      });
    });
  }, []);

  // `intoDir` lets a FOLDER download put each file in its own subdirectory.
  // The containment root sent to Rust stays `s.localPath` regardless — the
  // folder the user chose — so a nested target is still checked against it.
  const startDownload = useCallback((entry, intoDir, meta) => {
    const s = useFtpStore.getState();
    const connId = pinnedConn(meta, s);
    if (!connId || s.connState[connId] !== 'connected' || !s.localPath) return;
    const transferId = newTransferId();
    s.upsertTransfer({
      ...rowMeta(meta),
      transferId,
      connId,
      kind: 'download',
      name: entry.name,
      path: entry.path,
      transferred: 0,
      total: Number(entry.size) || 0,
      done: false,
      error: null,
      canceled: false,
    });
    registerRetry(transferId, () => startDownload(entry, intoDir, meta));
    invoke('ftp_download', {
      connId,
      transferId,
      remotePath: entry.path,
      localPath: joinLocal(intoDir || s.localPath, entry.name),
      // Sent so Rust can containment-check the destination against the folder
      // the user actually chose, instead of trusting this pre-joined path.
      localDir: s.localPath,
    }).catch((e) => {
      useFtpStore.getState().upsertTransfer({
        transferId,
        connId,
        kind: 'download',
        name: entry.name,
        transferred: 0,
        total: Number(entry.size) || 0,
        done: true,
        error: errText(e),
        canceled: false,
      });
    });
  }, []);

  /** OS drop (Finder → REMOTE pane): File blob, no path — stream it up in
   *  4 MB base64 chunks through ftp_upload_begin/_chunk/_finish. */
  const uploadBrowserFile = useCallback(async (file, targetDir, meta) => {
    const s = useFtpStore.getState();
    const connId = pinnedConn(meta, s);
    if (!connId || s.connState[connId] !== 'connected') return;
    if (wasCancelledWhileQueued(meta)) return;
    const remoteDir = targetDir || meta?.remoteDir || s.getRemotePath();
    const transferId = meta?.transferId || newTransferId();
    // Capture the store generation; if reset() bumps it (logout) or the view
    // unmounts, the loop stops emitting instead of repopulating a fresh store.
    const gen = s.generation;
    const stillAlive = () => aliveRef.current && useFtpStore.getState().generation === gen;
    // Only the row-creating upsert carries the full shape; subsequent upserts
    // send MINIMAL patches so they can never resurrect terminal flags.
    s.upsertTransfer({
      ...rowMeta(meta),
      transferId,
      connId,
      kind: 'upload',
      name: file.name,
      path: joinRemote(remoteDir, file.name),
      total: file.size,
      transferred: 0,
      done: false,
      error: null,
      canceled: false,
      queued: false,
    });
    registerRetry(transferId, () =>
      uploadBrowserFile(file, targetDir, { ...meta, transferId: undefined })
    );
    try {
      await invoke('ftp_upload_begin', {
        connId,
        transferId,
        remotePath: joinRemote(remoteDir, file.name),
        size: file.size,
      });
      for (let offset = 0; offset < file.size; offset += UPLOAD_CHUNK_SIZE) {
        if (!stillAlive()) return;
        // Stop if the transfer went terminal (cancelled by the user, or a raced
        // terminal event from Rust) — consult the live store row, not a stale
        // local snapshot.
        const cur = useFtpStore.getState().transfers.find((tr) => tr.transferId === transferId);
        if (!cur || cur.canceled || cur.done) return;
        const end = Math.min(offset + UPLOAD_CHUNK_SIZE, file.size);
        const buf = await file.slice(offset, end).arrayBuffer();
        if (!stillAlive()) return;
        await invoke('ftp_upload_chunk', { transferId, dataBase64: arrayBufferToBase64(buf) });
        if (!stillAlive()) return;
        useFtpStore.getState().upsertTransfer({ transferId, transferred: end });
      }
      if (!stillAlive()) return;
      await invoke('ftp_upload_finish', { transferId });
      if (!stillAlive()) return;
      // Rust emits the terminal done event too — this local upsert just makes
      // the row terminal even if that event raced the unmount.
      useFtpStore
        .getState()
        .upsertTransfer({ transferId, transferred: file.size, done: true, error: null });
      const after = useFtpStore.getState();
      if (after.activeConnId === connId && after.getRemotePath() === remoteDir) {
        after.listRemote(remoteDir).catch(() => {});
      }
    } catch (e) {
      if (!stillAlive()) return;
      const msg = errText(e);
      // A cancel manifests as the backend rejecting the next chunk; render it
      // as a cancelled row, not a red error row.
      if (/transfer canceled|unknown transfer/i.test(msg)) {
        useFtpStore.getState().upsertTransfer({ transferId, done: true, canceled: true, error: null });
      } else {
        useFtpStore.getState().upsertTransfer({ transferId, done: true, error: msg });
        invoke('ftp_transfer_cancel', { transferId }).catch(() => {});
      }
    }
  }, [aliveRef]);

  /* ── Overwrite protection ─────────────────────────────────────────────── */

  // Pending "this file already exists" questions, oldest first. A QUEUE, not a
  // single slot: two drops in quick succession would otherwise overwrite the
  // first question's resolver, and that batch would wait forever.
  const [conflictQueue, setConflictQueue] = useState([]);
  const askConflict = useCallback(
    (question) => new Promise((resolve) => setConflictQueue((q) => [...q, { ...question, resolve }])),
    []
  );
  const answerConflict = useCallback((answer) => {
    setConflictQueue((q) => {
      const [head, ...rest] = q;
      if (head) head.resolve(answer);
      return rest;
    });
  }, []);

  /**
   * Run a batch of single-file uploads or downloads with overwrite protection.
   *
   * Every transfer used to replace a same-named file silently: `sftp.create`
   * and `File::create` both truncate. Now the destination is listed ONCE,
   * clashes are put to the user (Replace / Skip / ... all), and only then do the
   * approved transfers start. The deciding is `utils/transferConflicts.js`,
   * executed in tests over every branch.
   *
   * The listing is fetched fresh with hidden files INCLUDED, not read from the
   * pane: with "show hidden files" off the pane does not list `.htaccess`, and
   * checking against it would let an upload of one replace the real one unasked.
   *
   * `destDir` (uploads only): a folder row the files were dropped ONTO; the
   * pane's current directory otherwise. The clash check lists that folder.
   *
   * @param {'upload'|'download'} kind
   * @param {Array<{name: string, start: (meta?: object) => (void|Promise<void>)}>} items
   * @param {string} [destDir]
   */
  const transferBatch = useCallback(
    async (kind, items, destDir) => {
      if (!items || items.length === 0) return;
      const s = useFtpStore.getState();
      // Pinned NOW: the questions below can stay open while the user switches
      // to another connected server, and the files belong where they were checked.
      const pin = { connId: s.activeConnId, remoteDir: kind === 'upload' ? destDir || s.getRemotePath() : undefined };
      let entries;
      try {
        entries =
          kind === 'upload'
            ? await invoke('ftp_list_dir', {
                connId: pin.connId,
                path: pin.remoteDir,
                showHidden: true,
              })
            : (await invoke('ftp_local_list', { path: s.localPath }))?.entries;
      } catch (err) {
        // Without the destination listing there is no way to know what would
        // be replaced. Refusing the batch beats overwriting blind.
        showNotice(errText(err));
        return;
      }
      const existing = existingNames(entries);
      const plan = await planTransfers({
        items,
        existing,
        ask: (question) => askConflict({ ...question, kind }),
      });
      // Sequential: a browser-file upload streams through JS, and starting a
      // batch of them at once would hold every file in memory together.
      for (const item of plan.toStart) {
        // `replaced` puts "Replaced" on the finished row instead of "Done".
        // eslint-disable-next-line no-await-in-loop
        await item.start({ ...pin, replaced: existing.has(item.name) });
      }
      // A skip is an outcome too: it gets a row, so the strip says what
      // happened to every file the user dropped.
      for (const name of plan.skipped) {
        useFtpStore.getState().upsertTransfer({
          transferId: newTransferId(),
          connId: s.activeConnId,
          kind,
          name,
          transferred: 0,
          total: 0,
          done: true,
          skipped: true,
          error: null,
          canceled: false,
        });
      }
      if (plan.blocked.length) {
        showNotice(t('ftp.conflictBlocked', { names: plan.blocked.join(', ') }));
      }
    },
    [askConflict, showNotice, t]
  );

  return {
    startPathUpload, startDownload, uploadBrowserFile, conflictQueue, askConflict, answerConflict,
    transferBatch,
  };
}
